import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import "firebase/compat/auth";
import resolveConfig from "../util/resolve-config";

const FUNCTIONS_REGION = "us-central1";

/**
 * Fetches the sample ledger for a period. The callable enforces super-admin
 * access and refuses a range matching more samples than it will read, so a
 * `failed-precondition` error here carries the matched total in `details`.
 *
 * @param {string} startDate ISO 8601, inclusive
 * @param {string} endDate ISO 8601, exclusive
 * @param {{organizationId?: string, authorId?: string}} filters
 * @return {Promise<object>}
 */
export async function fetchSampleReport(startDate, endDate, filters = {}) {
  const fn = firebase.functions().httpsCallable("getSampleReport");

  const result = await fn({
    startDate: startDate,
    endDate: endDate,
    organizationId: filters.organizationId || null,
    authorId: filters.authorId || null,
  });

  return result.data;
}

function exportUrl(startDate, endDate, filters) {
  const { projectId } = resolveConfig().firebase;
  const params = new URLSearchParams({ start: startDate, end: endDate });

  if (filters.organizationId) {
    params.set("organizationId", filters.organizationId);
  }

  if (filters.authorId) {
    params.set("authorId", filters.authorId);
  }

  return (
    `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net` +
    `/exportSampleCsv?${params.toString()}`
  );
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Downloads the complete ledger as CSV.
 *
 * This covers every matching sample rather than the capped set the interactive
 * report returns, so it is the only way to get a full period at the volumes
 * this system records. The endpoint streams, but the browser still buffers the
 * response before saving it, so a multi-year export is large.
 *
 * The request carries an ID token rather than a cookie, which is why the
 * download goes through fetch instead of pointing the browser at the URL.
 *
 * @param {string} startDate ISO 8601, inclusive
 * @param {string} endDate ISO 8601, exclusive
 * @param {{organizationId?: string, authorId?: string}} filters
 * @param {string} filename
 * @return {Promise<void>}
 */
export async function downloadSampleCsv(
  startDate,
  endDate,
  filters = {},
  filename = "sample-ledger.csv"
) {
  const user = firebase.auth().currentUser;

  if (!user) {
    throw new Error("You must be signed in to export samples.");
  }

  const token = await user.getIdToken();

  const response = await fetch(exportUrl(startDate, endDate, filters), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const failure = await response.json().catch(() => ({}));

    throw new Error(failure.error ?? "The export failed.");
  }

  saveBlob(await response.blob(), filename);
}
